import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  of,
  tap,
} from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';
import { SuperAdminAuthService } from './super-admin-auth.service';
import { PermissionService } from './permission.service';
import { AuthSessionRedirectService } from '../auth/auth-session-redirect.service';

export interface PlatformNotificationItem {
  id: number;
  type: string;
  title: string;
  message: string;
  meta: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
}

export interface PlatformNotificationListData {
  data: PlatformNotificationItem[];
  total: number;
  page: number;
  pageSize: number;
  unreadCount: number;
}

@Injectable({ providedIn: 'root' })
export class PlatformNotificationService implements OnDestroy {
  private readonly permissionService = inject(PermissionService);
  private readonly authSessionRedirect = inject(AuthSessionRedirectService);
  private readonly auth = inject(SuperAdminAuthService);
  private readonly ngZone = inject(NgZone);
  private readonly apiBase = environment.apiBaseUrl.replace(/\/$/, '');
  private readonly wsBase =
    (environment as { wsBaseUrl?: string }).wsBaseUrl?.replace(/\/$/, '') ??
    this.apiBase.replace(/\/api\/v1$/, '');

  private socket: Socket | null = null;
  private readonly notifications$ = new BehaviorSubject<
    PlatformNotificationItem[]
  >([]);
  private readonly unreadCount$ = new BehaviorSubject<number>(0);
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly http: HttpClient) {}

  get notifications(): Observable<PlatformNotificationItem[]> {
    return this.notifications$.asObservable();
  }

  get unreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  connectLive(): void {
    if (this.socket?.connected) {
      return;
    }
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
      return;
    }

    this.socket = io(this.wsBase, {
      path: '/socket.io/',
      withCredentials: true,
      // polling first — completes HTTP cookie handshake before WebSocket upgrade
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
    });

    this.socket.on('connect', () => {
      if (!environment.production) {
        console.debug('[PlatformNotificationService] WebSocket connected');
      }
    });

    this.socket.on('connect_error', (err: Error) => {
      if (!environment.production) {
        console.warn('[PlatformNotificationService] WebSocket connect_error:', err.message);
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      if (!environment.production) {
        console.debug('[PlatformNotificationService] WebSocket disconnected:', reason);
      }
    });

    this.attachSocketHandlers();
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  private attachSocketHandlers(): void {
    if (!this.socket) {
      return;
    }

    this.socket.on('notification:new', (payload: PlatformNotificationItem) => {
      this.ngZone.run(() => {
        if (payload.is_read) {
          return;
        }
        const next = [
          payload,
          ...this.notifications$.value.filter((n) => n.id !== payload.id),
        ];
        this.setUnreadList(next.slice(0, 50));
      });
    });

    this.socket.on('notification:read', (payload: { id: number }) => {
      this.ngZone.run(() => this.removeFromUnreadList(payload.id));
    });

    this.socket.on(
      'permissions:updated',
      (_payload: { userId?: number; roleId?: number; reason?: string }) => {
        this.ngZone.run(() => {
          this.permissionService.refreshPermissions().subscribe();
        });
      },
    );

    this.socket.on(
      'session:revoked',
      (payload: { reason?: string; message?: string }) => {
        this.ngZone.run(() => {
          if (!environment.production && payload?.reason) {
            console.debug('[PlatformNotificationService] session revoked:', payload.reason);
          }
          this.disconnect();
          this.auth
            .logout()
            .pipe(
              catchError(() => of(undefined)),
              finalize(() => this.authSessionRedirect.redirectToLogin()),
            )
            .subscribe();
        });
      },
    );
  }

  /** Dropdown only shows unread items; this list is unread-only. */
  private setUnreadList(items: PlatformNotificationItem[]): void {
    const unread = items.filter((n) => !n.is_read);
    this.notifications$.next(unread);
    this.unreadCount$.next(unread.length);
  }

  private removeFromUnreadList(id: number): void {
    const next = this.notifications$.value.filter((n) => n.id !== id);
    this.setUnreadList(next);
  }

  /**
   * Read-only fetch for the full notifications page.
   * Does NOT mutate the header bell state (unlike `loadNotifications`).
   */
  listNotifications(
    page: number,
    pageSize: number,
    unreadOnly = false,
  ): Observable<PlatformNotificationListData | null> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (unreadOnly) {
      params = params.set('unreadOnly', 'true');
    }
    return this.http
      .get<ApiEnvelope<PlatformNotificationListData>>(
        `${this.apiBase}/platform-notifications`,
        { params, withCredentials: true },
      )
      .pipe(
        map((res) => res.data ?? null),
        catchError(() => of(null)),
      );
  }

  loadNotifications(unreadOnly = true): Observable<PlatformNotificationItem[]> {
    let params = new HttpParams().set('page', '1').set('pageSize', '50');
    if (unreadOnly) {
      params = params.set('unreadOnly', 'true');
    }
    return this.http
      .get<ApiEnvelope<PlatformNotificationListData>>(
        `${this.apiBase}/platform-notifications`,
        { params, withCredentials: true },
      )
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if (data) {
            const unread = (data.data ?? []).filter((n) => !n.is_read);
            this.setUnreadList(unread);
          }
        }),
        map((data) => data?.data ?? []),
        catchError(() => of([])),
      );
  }

  markRead(id: number): Observable<void> {
    this.removeFromUnreadList(id);
    return this.http
      .patch<ApiEnvelope<unknown>>(
        `${this.apiBase}/platform-notifications/${id}/read`,
        {},
        { withCredentials: true },
      )
      .pipe(
        map(() => undefined),
        catchError(() => {
          this.loadNotifications(true).subscribe();
          return of(undefined);
        }),
      );
  }

  markAllRead(): Observable<void> {
    this.setUnreadList([]);
    return this.http
      .post<ApiEnvelope<unknown>>(
        `${this.apiBase}/platform-notifications/read-all`,
        {},
        { withCredentials: true },
      )
      .pipe(
        map(() => undefined),
        catchError(() => {
          this.loadNotifications(true).subscribe();
          return of(undefined);
        }),
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
